// src/components/Integrations/BigQueryFormsTab.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import {
  discardBigQueryFormDraft,
  publishBigQueryFormConfig,
  saveBigQueryFormConfig,
} from './integrationFunctions';

const DEFAULT_EXPORT_SCOPE = 'selected_published_forms';

const FILTERS = {
  ALL: 'all',
  EXPORTING: 'exporting',
  NOT_EXPORTING: 'not_exporting',
  DRAFT_PENDING: 'draft_pending',
  EXPLICIT_ENABLED: 'explicit_enabled',
  EXPLICIT_DISABLED: 'explicit_disabled',
};

const BOOLEAN_EXPORT_SHAPE_FIELDS = [
  'includeSubmissionEvents',
  'includeSubmissionAnswers',
  'includeProviderEvents',
  'includeAssets',
  'includeSubmissionActions',
  'includeProviderOutputs',
  'includeSystemMetadata',
];

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function getErrorMessage(error) {
  return error?.message || String(error || 'Something went wrong.');
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  try {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString();
  } catch (err) {
    return String(value);
  }
}

function isPlainObject(value) {
  return Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value);
}

function getFormId(form) {
  return (
    form?.id ||
    form?.formId ||
    form?.docId ||
    form?.documentId ||
    ''
  );
}

function getFormName(form) {
  return (
    form?.name ||
    form?.title ||
    form?.formName ||
    form?.displayName ||
    getFormId(form) ||
    'Untitled form'
  );
}

function getFormDescription(form) {
  return (
    form?.description ||
    form?.formDescription ||
    ''
  );
}

function isPublishedForm(form) {
  const status = String(form?.status || form?.publishStatus || '').toLowerCase();

  return (
    form?.published === true ||
    form?.isPublished === true ||
    status === 'published' ||
    status === 'live'
  );
}

function getProviderConfig(form, localConfigsByFormId) {
  const formId = getFormId(form);

  return (
    localConfigsByFormId[formId] ||
    form?.providerConfig ||
    form?.bigQueryConfig ||
    null
  );
}

function getFormConfigDraft(formConfig) {
  return (
    formConfig?.draftOverlay ||
    formConfig?.draft ||
    formConfig?.configDraft ||
    formConfig?.formConfigDraft ||
    null
  );
}

function getPublishedFormConfig(formConfig) {
  return (
    formConfig?.published ||
    formConfig?.publishedConfig ||
    formConfig?.config ||
    formConfig?.formConfig ||
    null
  );
}

function getResolvedFormConfig(formConfig) {
  return (
    formConfig?.resolved ||
    getPublishedFormConfig(formConfig) ||
    null
  );
}

function getFormExportMode(config) {
  const rawMode = normalizeString(
    config?.formExportMode ||
      config?.exportMode ||
      config?.overrideMode,
  ).toLowerCase();

  if (
    rawMode === 'enabled' ||
    rawMode === 'disabled' ||
    rawMode === 'inherit'
  ) {
    return rawMode;
  }

  if (config?.enabled === true || config?.exportEnabled === true) {
    return 'enabled';
  }

  if (config?.enabled === false || config?.exportEnabled === false) {
    return 'disabled';
  }

  return 'inherit';
}

function getEffectiveExportState({
  formPublished,
  exportScope,
  publishedMode,
}) {
  if (!formPublished) {
    return {
      exporting: false,
      reason: 'form_not_published',
      label: 'Not published',
      color: 'default',
    };
  }

  if (exportScope === 'disabled') {
    return {
      exporting: false,
      reason: 'org_policy_disabled',
      label: 'Blocked by org policy',
      color: 'default',
    };
  }

  if (publishedMode === 'enabled') {
    return {
      exporting: true,
      reason: 'form_override_enabled',
      label: 'Exporting',
      color: 'success',
    };
  }

  if (publishedMode === 'disabled') {
    return {
      exporting: false,
      reason: 'form_override_disabled',
      label: 'Disabled',
      color: 'default',
    };
  }

  if (
    exportScope === 'all_published_forms' ||
    exportScope === 'all_published_forms_except_excluded'
  ) {
    return {
      exporting: true,
      reason: 'org_policy_inherited',
      label: 'Exporting via org policy',
      color: 'success',
    };
  }

  return {
    exporting: false,
    reason: 'selected_forms_requires_enablement',
    label: 'Not enabled',
    color: 'default',
  };
}

function getModeChipProps(mode) {
  if (mode === 'enabled') {
    return {
      label: 'Explicitly enabled',
      color: 'success',
      variant: 'filled',
    };
  }

  if (mode === 'disabled') {
    return {
      label: 'Explicitly disabled',
      color: 'default',
      variant: 'filled',
    };
  }

  return {
    label: 'Inherits org policy',
    color: 'default',
    variant: 'outlined',
  };
}

function buildSearchIndex(form) {
  return [
    getFormId(form),
    getFormName(form),
    getFormDescription(form),
    form?.status,
    form?.organizationId,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function copyBooleanField({
  target,
  source,
  field,
}) {
  if (typeof source?.[field] === 'boolean') {
    target[field] = source[field];
  }
}

function pruneUndefined(value) {
  return Object.entries(value || {}).reduce((acc, [key, item]) => {
    if (item !== undefined) {
      acc[key] = item;
    }

    return acc;
  }, {});
}

function buildBigQueryFormConfigPayload({
  providerConfig,
  formExportMode,
}) {
  const publishedConfig = getPublishedFormConfig(providerConfig);
  const resolvedConfig = getResolvedFormConfig(providerConfig);
  const baseConfig = isPlainObject(publishedConfig) ?
    publishedConfig :
    isPlainObject(resolvedConfig) ?
      resolvedConfig :
      {};

  const explicitEnabled = formExportMode === 'enabled';

  const payload = {
    // Canonical field used by the BigQuery backend.
    formExportMode,

    // Compatibility fields retained because the existing backend and older UI
    // bridges still understand enabled/exportEnabled.
    enabled: explicitEnabled,
    exportEnabled: explicitEnabled,
  };

  if (normalizeString(baseConfig.deliveryMode)) {
    payload.deliveryMode = normalizeString(baseConfig.deliveryMode);
  }

  BOOLEAN_EXPORT_SHAPE_FIELDS.forEach((field) => {
    copyBooleanField({
      target: payload,
      source: baseConfig,
      field,
    });
  });

  if (Array.isArray(baseConfig.selectedFieldUfis)) {
    payload.selectedFieldUfis = baseConfig.selectedFieldUfis
      .map((ufi) => normalizeString(ufi))
      .filter(Boolean);
  }

  // Important:
  // Do not send dryRun from the form table. Dry-run behavior is governed by
  // org-level export policy unless a future UI explicitly introduces a
  // per-form dry-run override.
  delete payload.dryRun;

  return pruneUndefined(payload);
}

function getPolicyHelpText(exportScope) {
  if (exportScope === 'all_published_forms') {
    return 'All published forms are eligible by default. Use a row action only when you need an explicit override.';
  }

  if (exportScope === 'all_published_forms_except_excluded') {
    return 'Published forms export by default unless explicitly disabled.';
  }

  if (exportScope === 'disabled') {
    return 'The org policy currently disables BigQuery export. Form settings can be prepared, but future submissions will not export until policy is re-enabled.';
  }

  return 'Only published forms explicitly enabled in this table export to BigQuery.';
}

function getRows({
  forms,
  localConfigsByFormId,
  exportScope,
}) {
  return (Array.isArray(forms) ? forms : [])
    .map((form) => {
      const formId = getFormId(form);
      const providerConfig = getProviderConfig(form, localConfigsByFormId);
      const publishedConfig = getPublishedFormConfig(providerConfig);
      const draftConfig = getFormConfigDraft(providerConfig);
      const resolvedConfig = getResolvedFormConfig(providerConfig);
      const formPublished = isPublishedForm(form);

      const publishedMode = getFormExportMode(publishedConfig);
      const draftMode = draftConfig ? getFormExportMode(draftConfig) : null;
      const resolvedMode = getFormExportMode(resolvedConfig);

      const effectiveState = getEffectiveExportState({
        formPublished,
        exportScope,
        publishedMode,
      });

      return {
        form,
        formId,
        name: getFormName(form),
        description: getFormDescription(form),
        formPublished,
        providerConfig,
        publishedConfig,
        draftConfig,
        resolvedConfig,
        publishedMode,
        draftMode,
        resolvedMode,
        hasDraft: Boolean(draftConfig),
        effectiveState,
        searchIndex: buildSearchIndex(form),
      };
    })
    .filter((row) => row.formId);
}

function filterRows({
  rows,
  query,
  filter,
}) {
  const safeQuery = normalizeString(query).toLowerCase();

  return rows.filter((row) => {
    if (safeQuery && !row.searchIndex.includes(safeQuery)) {
      return false;
    }

    if (filter === FILTERS.EXPORTING) {
      return row.effectiveState.exporting === true;
    }

    if (filter === FILTERS.NOT_EXPORTING) {
      return row.effectiveState.exporting !== true;
    }

    if (filter === FILTERS.DRAFT_PENDING) {
      return row.hasDraft;
    }

    if (filter === FILTERS.EXPLICIT_ENABLED) {
      return row.publishedMode === 'enabled';
    }

    if (filter === FILTERS.EXPLICIT_DISABLED) {
      return row.publishedMode === 'disabled';
    }

    return true;
  });
}

function getStatusSummary(rows) {
  return rows.reduce(
    (acc, row) => {
      if (row.effectiveState.exporting) {
        acc.exporting += 1;
      } else {
        acc.notExporting += 1;
      }

      if (row.hasDraft) {
        acc.draftPending += 1;
      }

      if (row.publishedMode === 'enabled') {
        acc.explicitEnabled += 1;
      }

      if (row.publishedMode === 'disabled') {
        acc.explicitDisabled += 1;
      }

      return acc;
    },
    {
      exporting: 0,
      notExporting: 0,
      draftPending: 0,
      explicitEnabled: 0,
      explicitDisabled: 0,
    },
  );
}

export default function BigQueryFormsTab({
  organizationId,
  isBigQueryConnected = false,
  exportScope = DEFAULT_EXPORT_SCOPE,
  policyDryRun = true,

  forms = [],
  formsLoading = false,
  formsError = null,
  hasMoreForms = false,
  loadingMoreForms = false,
  onLoadMoreForms,
  onRefreshForms,

  onChanged,
}) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState(FILTERS.ALL);
  const [localConfigsByFormId, setLocalConfigsByFormId] = useState({});
  const [busyByFormId, setBusyByFormId] = useState({});
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setLocalConfigsByFormId({});
    setBusyByFormId({});
    setNotice('');
    setError('');
  }, [organizationId]);

  const rows = useMemo(() => {
    return getRows({
      forms,
      localConfigsByFormId,
      exportScope,
    });
  }, [
    exportScope,
    forms,
    localConfigsByFormId,
  ]);

  const visibleRows = useMemo(() => {
    return filterRows({
      rows,
      query,
      filter,
    });
  }, [
    filter,
    query,
    rows,
  ]);

  const summary = useMemo(() => {
    return getStatusSummary(rows);
  }, [rows]);

  const setRowBusy = useCallback((formId, busyLabel) => {
    setBusyByFormId((prev) => {
      if (!busyLabel) {
        const next = { ...prev };
        delete next[formId];
        return next;
      }

      return {
        ...prev,
        [formId]: busyLabel,
      };
    });
  }, []);

  const refreshForms = useCallback(() => {
    if (typeof onRefreshForms === 'function') {
      onRefreshForms();
    }

    if (typeof onChanged === 'function') {
      onChanged();
    }
  }, [
    onChanged,
    onRefreshForms,
  ]);

  const applyFormMode = useCallback(async ({
    row,
    formExportMode,
    actionLabel,
  }) => {
    setError('');
    setNotice('');

    if (!organizationId) {
      setError('No organization found for this session.');
      return;
    }

    if (!row?.formId) {
      setError('A valid form is required before updating BigQuery export.');
      return;
    }

    if (!row.formPublished) {
      setError('Only published forms can be configured for BigQuery export.');
      return;
    }

    const payload = buildBigQueryFormConfigPayload({
      providerConfig: row.providerConfig,
      formExportMode,
    });

    let savedConfig = null;

    try {
      setRowBusy(row.formId, actionLabel || 'Updating');

      savedConfig = await saveBigQueryFormConfig({
        organizationId,
        formId: row.formId,
        config: payload,
      });

      if (savedConfig) {
        setLocalConfigsByFormId((prev) => ({
          ...prev,
          [row.formId]: savedConfig,
        }));
      }

      const publishedConfig = await publishBigQueryFormConfig({
        organizationId,
        formId: row.formId,
      });

      setLocalConfigsByFormId((prev) => ({
        ...prev,
        [row.formId]: publishedConfig || savedConfig || row.providerConfig,
      }));

      const nextLabel =
        formExportMode === 'enabled'
          ? 'enabled'
          : formExportMode === 'disabled'
            ? 'disabled'
            : 'reset to inherit org policy';

      setNotice(`${row.name} was ${nextLabel} for BigQuery export.`);
      refreshForms();
    } catch (err) {
      if (savedConfig) {
        setLocalConfigsByFormId((prev) => ({
          ...prev,
          [row.formId]: savedConfig,
        }));
      }

      console.error('[BigQueryFormsTab] Failed to update form config:', {
        formId: row.formId,
        formName: row.name,
        formExportMode,
        message: getErrorMessage(err),
      });

      setError(
        getErrorMessage(err) ||
          'Failed to update the BigQuery form export setting.',
      );
    } finally {
      setRowBusy(row.formId, null);
    }
  }, [
    organizationId,
    refreshForms,
    setRowBusy,
  ]);

  const handleToggle = useCallback((row, checked) => {
    applyFormMode({
      row,
      formExportMode: checked ? 'enabled' : 'disabled',
      actionLabel: checked ? 'Enabling' : 'Disabling',
    });
  }, [applyFormMode]);

  const handleResetToInherit = useCallback((row) => {
    applyFormMode({
      row,
      formExportMode: 'inherit',
      actionLabel: 'Resetting',
    });
  }, [applyFormMode]);

  const handlePublishDraft = useCallback(async (row) => {
    setError('');
    setNotice('');

    if (!organizationId) {
      setError('No organization found for this session.');
      return;
    }

    if (!row?.formId) {
      setError('A valid form is required before publishing a draft.');
      return;
    }

    try {
      setRowBusy(row.formId, 'Publishing');

      const publishedConfig = await publishBigQueryFormConfig({
        organizationId,
        formId: row.formId,
      });

      setLocalConfigsByFormId((prev) => ({
        ...prev,
        [row.formId]: publishedConfig || row.providerConfig,
      }));

      setNotice(`${row.name} BigQuery draft was published.`);
      refreshForms();
    } catch (err) {
      console.error('[BigQueryFormsTab] Failed to publish draft:', {
        formId: row.formId,
        formName: row.name,
        message: getErrorMessage(err),
      });

      setError(
        getErrorMessage(err) ||
          'Failed to publish the BigQuery form export draft.',
      );
    } finally {
      setRowBusy(row.formId, null);
    }
  }, [
    organizationId,
    refreshForms,
    setRowBusy,
  ]);

  const handleDiscardDraft = useCallback(async (row) => {
    setError('');
    setNotice('');

    if (!organizationId) {
      setError('No organization found for this session.');
      return;
    }

    if (!row?.formId) {
      setError('A valid form is required before discarding a draft.');
      return;
    }

    try {
      setRowBusy(row.formId, 'Discarding');

      const nextConfig = await discardBigQueryFormDraft({
        organizationId,
        formId: row.formId,
      });

      setLocalConfigsByFormId((prev) => ({
        ...prev,
        [row.formId]: nextConfig || row.providerConfig,
      }));

      setNotice(`${row.name} BigQuery draft was discarded.`);
      refreshForms();
    } catch (err) {
      console.error('[BigQueryFormsTab] Failed to discard draft:', {
        formId: row.formId,
        formName: row.name,
        message: getErrorMessage(err),
      });

      setError(
        getErrorMessage(err) ||
          'Failed to discard the BigQuery form export draft.',
      );
    } finally {
      setRowBusy(row.formId, null);
    }
  }, [
    organizationId,
    refreshForms,
    setRowBusy,
  ]);

  const formsErrorMessage = formsError ? getErrorMessage(formsError) : '';

  return (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
          >
            <Stack spacing={0.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Published forms
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Manage which published forms can export future submissions to
                BigQuery. Changes are saved and published by the backend using
                the same form-level lifecycle that powers the tested export flow.
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            >
              <Chip
                size="small"
                color="success"
                label={`${summary.exporting} exporting`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${summary.notExporting} not exporting`}
              />
              {summary.draftPending > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  label={`${summary.draftPending} draft pending`}
                />
              )}
            </Stack>
          </Stack>

          {!isBigQueryConnected && (
            <Alert severity="warning">
              Connect and validate the organization BigQuery destination before
              enabling forms for export.
            </Alert>
          )}

          <Alert severity={exportScope === 'disabled' ? 'warning' : 'info'}>
            <strong>Current export scope:</strong> {exportScope || DEFAULT_EXPORT_SCOPE}.{' '}
            {getPolicyHelpText(exportScope || DEFAULT_EXPORT_SCOPE)}
          </Alert>

          {policyDryRun && (
            <Alert severity="warning">
              Dry run mode is enabled in the org policy. Form settings can be
              prepared here, but eligible submissions will not write rows to
              BigQuery until dry run is off and the policy is published.
            </Alert>
          )}

          {notice && (
            <Alert severity="success" onClose={() => setNotice('')}>
              {notice}
            </Alert>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {formsErrorMessage && (
            <Alert severity="error">
              Forms could not be loaded: {formsErrorMessage}
            </Alert>
          )}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              size="small"
              fullWidth
              label="Search forms"
              placeholder="Search by form name or ID"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 } }}>
              <InputLabel id="bigquery-form-filter-label">
                Filter
              </InputLabel>

              <Select
                labelId="bigquery-form-filter-label"
                label="Filter"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              >
                <MenuItem value={FILTERS.ALL}>All forms</MenuItem>
                <MenuItem value={FILTERS.EXPORTING}>Exporting</MenuItem>
                <MenuItem value={FILTERS.NOT_EXPORTING}>Not exporting</MenuItem>
                <MenuItem value={FILTERS.DRAFT_PENDING}>Draft pending</MenuItem>
                <MenuItem value={FILTERS.EXPLICIT_ENABLED}>
                  Explicitly enabled
                </MenuItem>
                <MenuItem value={FILTERS.EXPLICIT_DISABLED}>
                  Explicitly disabled
                </MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={onRefreshForms}
              disabled={formsLoading || loadingMoreForms}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Refresh
            </Button>
          </Stack>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflowX: 'auto',
            }}
          >
            <Table size="small" aria-label="BigQuery form export settings">
              <TableHead>
                <TableRow>
                  <TableCell>Form</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Published setting</TableCell>
                  <TableCell>Draft</TableCell>
                  <TableCell align="right">Export toggle</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {formsLoading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        justifyContent="center"
                        sx={{ py: 4 }}
                      >
                        <CircularProgress size={22} />
                        <Typography variant="body2" color="text.secondary">
                          Loading published forms...
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {rows.length === 0
                            ? 'No published forms are available for BigQuery configuration.'
                            : 'No forms match the current search or filter.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((row) => {
                    const busyLabel = busyByFormId[row.formId] || '';
                    const rowBusy = Boolean(busyLabel);
                    const modeChipProps = getModeChipProps(row.publishedMode);
					const explicitEnabled = row.publishedMode === 'enabled';
					const effectiveExporting = row.effectiveState.exporting === true;
					const canToggle =
					  Boolean(organizationId) &&
					  isBigQueryConnected &&
					  row.formPublished &&
					  !rowBusy;

                    return (
                      <TableRow
                        key={row.formId}
                        hover
                        sx={{
                          '&:last-child td, &:last-child th': {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell sx={{ minWidth: 280 }}>
                          <Stack spacing={0.35}>
                            <Typography variant="body2" fontWeight={700}>
                              {row.name}
                            </Typography>

                            {row.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {row.description}
                              </Typography>
                            )}

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ wordBreak: 'break-all' }}
                            >
                              {row.formId}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                              Published: {formatDateTime(row.form?.publishedAt)}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            color={row.effectiveState.color}
                            label={row.effectiveState.label}
                          />
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.75} alignItems="flex-start">
                            <Chip
                              size="small"
                              color={modeChipProps.color}
                              variant={modeChipProps.variant}
                              label={modeChipProps.label}
                            />

                            {row.publishedConfig?.publishedAt && (
                              <Typography variant="caption" color="text.secondary">
                                Updated {formatDateTime(row.publishedConfig.publishedAt)}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>

                        <TableCell>
                          {row.hasDraft ? (
                            <Stack spacing={0.75} alignItems="flex-start">
                              <Chip
                                size="small"
                                color="warning"
                                label={`Draft: ${row.draftMode || 'pending'}`}
                              />

                              {row.draftConfig?.updatedAt && (
                                <Typography variant="caption" color="text.secondary">
                                  Saved {formatDateTime(row.draftConfig.updatedAt)}
                                </Typography>
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No draft
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Tooltip
                            title={
                              !isBigQueryConnected
                                ? 'Connect BigQuery before changing form export settings.'
                                : !row.formPublished
                                  ? 'Only published forms can export to BigQuery.'
								: effectiveExporting
								  ? 'Turn off BigQuery export for future submissions from this form.'
								  : 'Turn on BigQuery export for future submissions from this form.'                            }
                          >
                            <span>
								<Switch
								  checked={explicitEnabled}
                                onChange={(event) => {
                                  handleToggle(row, event.target.checked);
                                }}
                                disabled={!canToggle}
                                inputProps={{
                                  'aria-label': `Toggle BigQuery export for ${row.name}`,
                                }}
                              />
                            </span>
                          </Tooltip>

                          {rowBusy && (
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              justifyContent="flex-end"
                            >
                              <CircularProgress size={14} />
                              <Typography variant="caption" color="text.secondary">
                                {busyLabel}...
                              </Typography>
                            </Stack>
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {row.hasDraft && (
                              <>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handlePublishDraft(row)}
                                  disabled={rowBusy || !isBigQueryConnected}
                                >
                                  Publish draft
                                </Button>

                                <Button
                                  size="small"
                                  variant="text"
                                  color="warning"
                                  onClick={() => handleDiscardDraft(row)}
                                  disabled={rowBusy}
                                >
                                  Discard
                                </Button>
                              </>
                            )}

                            {row.publishedMode !== 'inherit' && (
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<RestartAltIcon fontSize="small" />}
                                onClick={() => handleResetToInherit(row)}
                                disabled={rowBusy || !isBigQueryConnected || !row.formPublished}
                              >
                                Inherit
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="caption" color="text.secondary">
              Showing {visibleRows.length} of {rows.length} loaded published forms.
            </Typography>

            {hasMoreForms && (
              <Button
                variant="outlined"
                onClick={onLoadMoreForms}
                disabled={formsLoading || loadingMoreForms}
              >
                {loadingMoreForms ? 'Loading more...' : 'Load more forms'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
